import React, { CSSProperties, FC, memo, useEffect, useMemo } from 'react'

import { motion } from 'motion/react'

import styles from './WindowContent.module.scss'
import { WindowContentProps } from './WindowContent.types'
import { normalizeUrl } from './utils'

export const WindowContent: FC<WindowContentProps> = ({
  window,
  iframeRef,
  src,
  isDragging,
  onFocusWindow
}) => {
  // inject container top dock height to iframe
  useEffect(() => {
    const iframe = iframeRef.current
    const handleClick = () => onFocusWindow?.()
    if (iframe) {
      const handleLoad = () => {
        const iframeDocument = iframe.contentDocument
        if (iframeDocument) {
          iframeDocument.documentElement.style.setProperty(
            '--container-top-dock-height',
            '0px' // because iframe is in a window
          )
          iframeDocument.addEventListener('click', handleClick, true)

          if (!iframeDocument.getElementById('container-hide-usercentrics')) {
            const style = iframeDocument.createElement('style')
            style.id = 'container-hide-usercentrics'
            style.textContent =
              '#usercentrics-root { display: none !important; }'
            iframeDocument.head.appendChild(style)
          }
        }
      }

      if (iframe.contentDocument?.readyState === 'complete') {
        handleLoad()
      }

      iframe.addEventListener('load', handleLoad)
      return () => {
        iframe.removeEventListener('load', handleLoad)
        iframe.contentDocument?.removeEventListener('click', handleClick, true)
      }
    }
  }, [iframeRef, onFocusWindow])

  return (
    <motion.div
      className={styles.windowContent}
      animate={{
        borderRadius: window.isMaximized ? '0px' : '0 0 1rem 1rem'
      }}
    >
      {window.localComponent ? (
        <div className={styles.componentWrapper}>
          <window.localComponent />
        </div>
      ) : (
        <MemoizedIframe
          id={`iframe-${window.id}`}
          iframeRef={iframeRef}
          src={src ? normalizeUrl(src) : src}
          title={window.title}
          className={styles.iframe}
          isDragging={isDragging}
        />
      )}
    </motion.div>
  )
}

const MemoizedIframe = memo(
  ({
    id,
    iframeRef,
    src,
    title,
    className,
    isDragging
  }: {
    id: string
    iframeRef: React.RefObject<HTMLIFrameElement>
    src?: string
    title: string
    className: string
    isDragging: boolean
  }) => {
    const iframeStyle = useMemo(
      () =>
        ({
          pointerEvents: isDragging ? 'none' : 'auto'
        }) as CSSProperties,
      [isDragging]
    )

    return (
      <iframe
        id={id}
        data-container-analytics-owner='parent'
        ref={iframeRef}
        src={src}
        title={title}
        className={className}
        style={iframeStyle}
      />
    )
  }
)
